import React, { useCallback, useMemo, useState } from 'react';
import { SourceItem } from "../../shared/models/source-item.model";
import { useSiteContext } from "../../state/SiteContext";
import { FilterActionName, useFilterAction, useFilterState, useSystemPropertyFilterStringValue } from "../../state/FilterContext";
import { TextExtractor, findFirstComponentOfType } from "../internal/TextExtractor";
import { ArrayProvider } from "../../state/ArrayContext";
import { BaseSelector, collectProps } from "../internal/BaseSelector";
import { SystemPropertyFilterValues } from '../source-components/array-sources/SystemPropertyFilterValues';
import { SystemPropertyValuesQuery } from '../source-components/array-sources/SystemPropertyValuesQuery';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { applyDefaults, getStateAsLocationQuery } from '../../state/state-url-management';
import { buildFilterRedirectNavigateTo, hasFilterRedirectNavigateIntent } from '../../shared/utils/filter-redirect-url.utils';
import { useToggleSwitchController } from '../../state/toggle-switch-controller.context';
import { activateToggleSwitchByName } from './toggle-switch-activation.utils';

interface SystemPropertySelectorProps {
  optionlabeltemplate: string,
  multiple: string,
  redirect?: string,
  redirectLayout?: string,
  activateToggleSwitch?: string,
  children: React.ReactNode,
}
/***
 * A selector component that allows the user to select a value from a list of values.
 * The list of values is loaded using an ArraySource specification.
 */
export const SystemPropertySelector = React.memo(function SystemPropertySelector({ optionlabeltemplate, multiple, redirect, redirectLayout = "", activateToggleSwitch, children }: SystemPropertySelectorProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const setSystemProperty = useFilterAction(FilterActionName.SET_SYSTEM_PROPERTY);
  const removeSystemProperty = useFilterAction(FilterActionName.REMOVE_SYSTEM_PROPERTY);
  const hidFilter = useSystemPropertyFilterStringValue("hid");
  const filterState = useFilterState();
  const toggleSwitchController = useToggleSwitchController();

  // Get the label from the label child
  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState<string | null>(null);

  // Get the property name from the property child
  const propertyChild = useMemo(() => findFirstComponentOfType(children, "arg-property"), [children]);
  const [propertyName, setPropertyName] = useState<string | null>(null);

  const { siteState } = useSiteContext();

  // Get the autoCompleteProps from the autoCompleteProps child
  const autoCompletePropsChild = useMemo(() => findFirstComponentOfType(children, "arg-autocompleteprops"), [children]);
  const autoCompleteProps = useMemo(() => collectProps(autoCompletePropsChild), [autoCompletePropsChild]);

  // Get the textfieldProps from the textfieldProps child
  const textfieldPropsChild = useMemo(() => findFirstComponentOfType(children, "arg-textfieldprops"), [children]);
  const textfieldProps = useMemo(() => collectProps(textfieldPropsChild), [textfieldPropsChild]);

  const isMultiple: boolean = multiple === "true";

  // Get the valueset array from the valueset child
  const [valueSetArray, setValueSetArray] = useState<Array<SourceItem>>([]);
  const valuesetChild = useMemo(() => findFirstComponentOfType(children, "arg-valueset"), [children]);

  // Get the initial values array from a SystemPropertFilter array source defined in the component output
  const [initialValuesArray, setInitialValuesArray] = useState<Array<SourceItem>>([]);
  const memoizedInitialValuesArray = useMemo(() => initialValuesArray, [initialValuesArray]);

  const onChangeFunc = useCallback((values: Array<SourceItem>) => {

    const intentsRedirectNavigate =
      hasFilterRedirectNavigateIntent(redirect, redirectLayout) && !!propertyName;
    let redirectToPath: string | null = null;
    if (intentsRedirectNavigate) {
      const filterStateCopy = { ...filterState };
      if (hidFilter) {
        delete filterStateCopy.systemProperties?.["hid"];
      }
      filterStateCopy.systemProperties = filterStateCopy.systemProperties || {};
      filterStateCopy.systemProperties[propertyName] = values.map(item => item.value);
      if (siteState?.site) {
        applyDefaults(filterStateCopy, siteState?.site);
      }
      const searchParams = getStateAsLocationQuery(filterStateCopy);
      redirectToPath = buildFilterRedirectNavigateTo({
        effectiveSitePath: siteState.sitePath ?? "",
        redirect,
        redirectLayout,
        currentPathname: location.pathname,
        searchParamsString: searchParams.toString(),
        locationHash: location.hash,
        activeToggles: activateToggleSwitch,
      });
    }
    if (values.length) {
      setSystemProperty(propertyName, values.map(item => item.value));
    }
    else {
      removeSystemProperty(propertyName);
    }
    if (hidFilter) {
      removeSystemProperty("hid");
    }
    activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "SystemPropertySelector");
    if (redirectToPath) {
      navigate(redirectToPath, { replace: true });
    }
  }, [
    activateToggleSwitch,
    removeSystemProperty,
    setSystemProperty,
    navigate,
    propertyName,
    redirect,
    redirectLayout,
    siteState.sitePath,
    siteState.site,
    filterState,
    hidFilter,
    location.hash,
    location.pathname,
    toggleSwitchController,
  ]);

  const memoizedInitialStringValues = useMemo(() => {
    return memoizedInitialValuesArray.map(item => item.value.toString());
  }, [memoizedInitialValuesArray]);

  return <>
    <BaseSelector
      label={labelValue || ""}
      valueset={valueSetArray}
      initialValues={memoizedInitialStringValues}
      optionlabeltemplate={optionlabeltemplate}
      autocompleteprops={autoCompleteProps}
      textfieldprops={textfieldProps}
      multiple={isMultiple}
      onChange={onChangeFunc}
    />

    <ArrayProvider setArray={setValueSetArray}>
      {valuesetChild?.props.children || <SystemPropertyValuesQuery propertyNameArg={propertyName} />}
    </ArrayProvider>
    <ArrayProvider setArray={setInitialValuesArray}>
      <SystemPropertyFilterValues propertyNameArg={propertyName} />
    </ArrayProvider>
    <TextExtractor onExtractedText={setLabelValue}>
      {labelChild?.props.children}
    </TextExtractor>
    <TextExtractor onExtractedText={setPropertyName}>
      {propertyChild?.props.children}
    </TextExtractor>
  </>
});